import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";

interface UtilizationDisplayProps {
  stackedUtilization: number; // Stacking efficiency (0-200%+)
  allUtilization: number; // Floor coverage (0-100%)
}

export const UtilizationDisplay: React.FC<UtilizationDisplayProps> = ({
  stackedUtilization,
  allUtilization,
}) => {
  const theme = useTheme();

  // Color based on baseline (100%): green if above, blue if below
  const primaryColor = stackedUtilization >= 100
    ? theme.palette.success.main
    : theme.palette.primary.main;
  const fillPercentage = Math.min(stackedUtilization, 200); // Cap at 200% for display

  return (
    <Stack spacing={0.5}>
      {/* Primary metric: Stacking efficiency */}
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            color: primaryColor,
          }}
        >
          {Math.round(stackedUtilization)}%
        </Typography>
        <Typography variant="caption" color="text.secondary">
          stacked
        </Typography>
      </Stack>

      {/* 0-200% gauge with minimal baseline marker */}
      <Box sx={{ position: "relative", width: "100px", height: 5, mt: 0.5 }}>
        {/* Thin baseline indicator line (horizontal) */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: 0,
            width: "100%",
            height: 1,
            bgcolor: "divider",
          }}
        />

        {/* Fill 0-100% (blue) */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: `${Math.min((fillPercentage / 200) * 100, 50)}%`,
            height: "100%",
            bgcolor: "primary.main",
            borderRadius: stackedUtilization >= 100 ? "100px 0 0 100px" : "100px",
            transition: "width 0.3s ease-in-out",
          }}
        />

        {/* Fill 100%+ (green) */}
        {stackedUtilization > 100 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50%",
              width: `${Math.min(((fillPercentage - 100) / 200) * 100, 50)}%`,
              height: "100%",
              bgcolor: "success.main",
              borderRadius: "0 100px 100px 0",
              transition: "width 0.3s ease-in-out",
            }}
          />
        )}

        {/* 100% baseline marker (vertical) */}
        <Box
          sx={{
            position: "absolute",
            top: -1,
            left: "50%",
            width: 2,
            height: 7,
            bgcolor: theme.palette.grey[500],
          }}
        />
      </Box>

      {/* Secondary metric: At full tenant load */}
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
        {Math.round(allUtilization)}% at full tenant load
      </Typography>
    </Stack>
  );
};
